import React, { useCallback, useContext, useEffect, useMemo, useRef, useState, createContext } from "react";
import * as PusherPushNotifications from "@pusher/push-notifications-web";
import HNotification from "../components/custom/notification/HNotification.jsx";
import { NOTIFICATION_PERMISSION_STATUS, SESSION_STORAGE_KEY_INBOX_PUSH_BANNER_DISMISSED } from "../constants/AppConstants.js";
import { AppContext } from "./AppContext";
import useHttp from "../hooks/useHttp";
import { checkIsInsideIframe, checkIsInternalMode } from "../pages/utils/commonUtils.js";

const BeamsContext = createContext();
export const useBeams = () => useContext(BeamsContext);

function readPushBannerDismissedFromSessionStorage() {
  try {
    return sessionStorage.getItem(SESSION_STORAGE_KEY_INBOX_PUSH_BANNER_DISMISSED) === "true";
  } catch {
    return false;
  }
}

const createInitialBeamsRegistrationRef = () => ({
  lastRegisteredOsyncUserId: null,
  isBeamsRegistrationInFlight: false,
  suppressRegisterUntilNewLogin: false,
});

export const BeamsProvider = React.memo(({ children }) => {
  const [appProperties] = useContext(AppContext);
  const { fetchData } = useHttp();
  const beamsClientRef = useRef(null);
  const beamsRegistrationRef = useRef(createInitialBeamsRegistrationRef());
  const lastEffectOsyncUserIdRef = useRef(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [isRegistered, setIsRegistered] = useState(false);
  const [pushBannerDismissedSession, setPushBannerDismissedSession] = useState(readPushBannerDismissedFromSessionStorage);
  const [notificationPermission, setNotificationPermission] = useState(() =>
    "Notification" in window ? Notification.permission : NOTIFICATION_PERMISSION_STATUS.UNSUPPORTED,
  );
  const [isRequestingPushPermission, setIsRequestingPushPermission] = useState(false);
  const isEmbeddedInIframe = useMemo(() => checkIsInsideIframe(), []);

  const currentUserId = useMemo(() => appProperties?.currentUser?.osyncUserId, [appProperties?.currentUser?.osyncUserId]);

  const checkPermission = useCallback(() => {
    if ("Notification" in window) {
      const permission = Notification.permission;
      setNotificationPermission(permission);
      return permission;
    }
    setNotificationPermission(NOTIFICATION_PERMISSION_STATUS.UNSUPPORTED);
    return NOTIFICATION_PERMISSION_STATUS.UNSUPPORTED;
  }, []);

  const requestPermission = useCallback(async () => {
    if (!("Notification" in window)) {
      console.warn("This browser does not support notifications.");
      return false;
    }

    if (Notification.permission === NOTIFICATION_PERMISSION_STATUS.GRANTED) {
      return true;
    }

    if (Notification.permission === NOTIFICATION_PERMISSION_STATUS.DENIED) {
      console.warn("Beams register: notification permission denied by user");
      return false;
    }

    try {
      const permission = await Notification.requestPermission();
      setNotificationPermission(permission);
      if (permission !== NOTIFICATION_PERMISSION_STATUS.GRANTED) {
        console.warn("Beams register: notification permission not granted", { permission });
      }
      return permission === NOTIFICATION_PERMISSION_STATUS.GRANTED;
    } catch (error) {
      console.error("Beams register: requestPermission failed", error);
      return false;
    }
  }, []);

  const initializeBeams = useCallback(async () => {
    if (beamsClientRef.current) {
      return true;
    }

    try {
      const instanceId = process.env.REACT_APP_PUSHER_BEAMS_INSTANCE_ID;
      if (!instanceId) {
        console.warn("Pusher Beams instance ID is not configured.");
        return false;
      }

      const serviceWorkerRegistration = await navigator.serviceWorker.register("/service-worker.js");
      await navigator.serviceWorker.ready;
      const beamsClient = new PusherPushNotifications.Client({
        instanceId,
        serviceWorkerRegistration,
      });

      beamsClientRef.current = beamsClient;
      return true;
    } catch (error) {
      console.error("Beams register: initializeBeams failed", error);
      beamsClientRef.current = null;
      setIsInitialized(false);
      return false;
    }
  }, []);

  const registerUser = useCallback(
    async (osyncUserId) => {
      const reg = beamsRegistrationRef.current;

      if (reg.isBeamsRegistrationInFlight) {
        console.warn("Beams register: skipped, registration already in progress", { osyncUserId });
        return false;
      }

      if (!osyncUserId) {
        console.warn("Beams register: skipped, osyncUserId is missing");
        return false;
      }

      reg.isBeamsRegistrationInFlight = true;

      try {
        if (Notification.permission === NOTIFICATION_PERMISSION_STATUS.DENIED) {
          console.warn("Beams register: aborted, notification permission not granted", { osyncUserId });
          return false;
        }

        console.warn("Beams register: starting", { osyncUserId });
        const initialized = await initializeBeams();
        if (!initialized) {
          console.error("Beams register: aborted, client did not initialize", { osyncUserId });
          return false;
        }

        const hasPermission = await requestPermission();
        if (!hasPermission) {
          console.warn("Beams register: aborted, no notification permission", { osyncUserId });
          return false;
        }

        const beamsUserId = await beamsClientRef.current.getUserId();
        if (beamsUserId === osyncUserId) {
          reg.lastRegisteredOsyncUserId = osyncUserId;
          reg.suppressRegisterUntilNewLogin = false;
          setIsRegistered(true);
          setIsInitialized(true);
          console.warn("Beams register: already associated with this user (SDK state)", { osyncUserId });
          return true;
        }

        if (beamsUserId && beamsUserId !== osyncUserId) {
          console.warn("Beams register: clearing SDK state for different Beams user before setUserId", {
            previousBeamsUserId: beamsUserId,
            osyncUserId,
          });
          await beamsClientRef.current.clearAllState();
          reg.suppressRegisterUntilNewLogin = false;
          setIsInitialized(true);
        } else if (!beamsUserId) {
          await beamsClientRef.current.start();
          reg.suppressRegisterUntilNewLogin = false;
          setIsInitialized(true);
        }

        const tokenProvider = {
          fetchToken: async () => {
            const response = await fetchData(`pusher/beams-auth`, "POST", null, appProperties);
            const parsed = typeof response === "string" ? JSON.parse(response) : response;
            const token = parsed?.data?.token ?? parsed?.token;

            if (!token || typeof token !== "string") {
              throw new Error("Beams auth: invalid or missing token in response");
            }

            return { token };
          },
        };

        await beamsClientRef.current.setUserId(osyncUserId, tokenProvider);

        reg.lastRegisteredOsyncUserId = osyncUserId;
        reg.suppressRegisterUntilNewLogin = false;
        setIsRegistered(true);

        console.warn("Beams register: success", { osyncUserId });
        return true;
      } catch (error) {
        console.error("Beams register: failed", { osyncUserId, error });
        setIsRegistered(false);
        return false;
      } finally {
        reg.isBeamsRegistrationInFlight = false;
      }
    },
    [initializeBeams, requestPermission, appProperties, fetchData],
  );

  const unregisterUser = useCallback(async () => {
    const reg = beamsRegistrationRef.current;
    const previousOsyncUserId = reg.lastRegisteredOsyncUserId;
    let stopError = null;
    try {
      if (beamsClientRef.current) {
        console.warn("Beams unregister: stopping client", { previousOsyncUserId });
        await beamsClientRef.current.stop();
      }
    } catch (error) {
      stopError = error;
      console.error("Beams unregister: stop failed", { previousOsyncUserId, error });
    } finally {
      beamsClientRef.current = null;
      reg.lastRegisteredOsyncUserId = null;
      reg.suppressRegisterUntilNewLogin = true;
      setIsInitialized(false);
      setIsRegistered(false);
      console.warn("Beams unregister: local state cleared", { previousOsyncUserId });
    }
    return stopError === null;
  }, []);

  const isInsideTeamsHost = isEmbeddedInIframe && appProperties?.controller?.service === "Teams";

  const shouldShowPushBanner = useMemo(() => {
    if (checkIsInternalMode()) {
      return false;
    }
    // Teams hosts the app in a cross-origin iframe where notification permission can never be granted.
    if (isInsideTeamsHost) {
      return false;
    }
    if (!("Notification" in window)) {
      return false;
    }
    if (pushBannerDismissedSession) {
      return false;
    }
    if (notificationPermission === NOTIFICATION_PERMISSION_STATUS.GRANTED) {
      return false;
    }
    return (
      notificationPermission === NOTIFICATION_PERMISSION_STATUS.DEFAULT ||
      notificationPermission === NOTIFICATION_PERMISSION_STATUS.DENIED
    );
  }, [isInsideTeamsHost, pushBannerDismissedSession, notificationPermission]);

  const handleDismissPushBanner = useCallback(() => {
    try {
      sessionStorage.setItem(SESSION_STORAGE_KEY_INBOX_PUSH_BANNER_DISMISSED, "true");
    } catch {
      // ignore session storage failures
    }
    setPushBannerDismissedSession(true);
  }, []);

  const handleEnablePushNotifications = useCallback(async () => {
    setIsRequestingPushPermission(true);
    try {
      const granted = await requestPermission();
      checkPermission();
      if (!granted) {
        if (isEmbeddedInIframe) {
          appProperties?.controller?.getUrlParamsForRedirection(true, appProperties).then(function (queryParams) {
            window.open("/inbox?" + queryParams, "_blank");
          });
        } else if (Notification.permission === NOTIFICATION_PERMISSION_STATUS.DENIED) {
          HNotification.notify({
            type: "info",
            message: "Enable notifications",
            description: "Notifications are blocked. Please allow notifications in your browser settings.",
          });
        }
        return;
      }
      checkPermission();
      if (currentUserId) {
        await registerUser(currentUserId);
      }
    } finally {
      setIsRequestingPushPermission(false);
    }
  }, [appProperties, checkPermission, currentUserId, isEmbeddedInIframe, registerUser, requestPermission]);

  useEffect(() => {
    try {
      const searchParams = new URLSearchParams(window.location.search);
      const isInternal =
        searchParams.has("internal") &&
        (searchParams.get("internal") === "true" || searchParams.get("internal") === true);
      if (isInternal) {
        return;
      }
    } catch (error) {
      console.error("Beams: internal mode check failed", error);
    }

    const reg = beamsRegistrationRef.current;
    const effectOsyncUserId = currentUserId ?? null;
    const previousEffectOsyncUserId = lastEffectOsyncUserIdRef.current;

    if (effectOsyncUserId !== previousEffectOsyncUserId) {
      if (effectOsyncUserId && (!previousEffectOsyncUserId || previousEffectOsyncUserId !== effectOsyncUserId)) {
        reg.suppressRegisterUntilNewLogin = false;
      }
      lastEffectOsyncUserIdRef.current = effectOsyncUserId;
    }

    if (currentUserId) {
      const shouldRegister =
        reg.lastRegisteredOsyncUserId !== currentUserId &&
        !reg.isBeamsRegistrationInFlight &&
        !reg.suppressRegisterUntilNewLogin;

      if (shouldRegister) {
        registerUser(currentUserId);
      }
    } else {
      const shouldUnregister = reg.lastRegisteredOsyncUserId && !reg.suppressRegisterUntilNewLogin;

      if (shouldUnregister) {
        unregisterUser();
      }
    }
  }, [currentUserId, registerUser, unregisterUser]);

  useEffect(() => {
    checkPermission();
  }, [checkPermission]);

  const beamsContext = useMemo(
    () => ({
      beamsClient: beamsClientRef.current,
      isInitialized,
      isRegistered,
      initializeBeams,
      registerUser,
      unregisterUser,
      requestPermission,
      checkPermission,
      shouldShowPushBanner,
      notificationPermission,
      isEmbeddedInIframe,
      isRequestingPushPermission,
      handleDismissPushBanner,
      handleEnablePushNotifications,
    }),
    [
      isInitialized,
      isRegistered,
      initializeBeams,
      registerUser,
      unregisterUser,
      requestPermission,
      checkPermission,
      shouldShowPushBanner,
      notificationPermission,
      isEmbeddedInIframe,
      isRequestingPushPermission,
      handleDismissPushBanner,
      handleEnablePushNotifications,
    ],
  );

  return <BeamsContext.Provider value={beamsContext}>{children}</BeamsContext.Provider>;
});
